import React, {useState, useEffect, useCallback, useMemo} from 'react';
import styled, {keyframes, css} from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {familyTreeData} from '../../familyTreeData';
import {db} from '../../firebase';
import {ref, get, push, set, remove, query, orderByChild, limitToLast} from 'firebase/database';

const MAX_QUESTIONS = 10;
const POINTS_FULL = 10;
const POINTS_HINT = 5;

const getEligiblePeople = () =>
    familyTreeData.filter(p => {
        const fn = p.data.firstName || '';
        return fn && fn !== 'אב' && fn !== 'אם' && fn.length > 1;
    });

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const personName = (id) => {
    const p = familyTreeData.find(x => x.id === id);
    if (!p) return id;
    return `${p.data.firstName || ''} ${p.data.lastName || ''}`.trim();
};

const personAvatar = (id) => {
    const p = familyTreeData.find(x => x.id === id);
    return p?.data?.avatar || null;
};

const buildOptions = (item, eligible) => {
    if (item.answerType === 'text') {
        const correct = {id: '__correct__', name: item.textCorrect, avatar: null, isText: true};
        const wrong = (item.textWrong || []).slice(0, 3).map((t, i) => ({
            id: `__wrong_${i}__`, name: t, avatar: null, isText: true,
        }));
        return shuffle([correct, ...wrong]);
    }

    const correctId = item.aboutPerson;
    const correct = {id: correctId, name: personName(correctId), avatar: personAvatar(correctId)};

    if (item.wrongOptions && item.wrongOptions.length >= 3) {
        const wrong = item.wrongOptions.slice(0, 3).map(w => ({
            id: w.id,
            name: personName(w.id) || w.name,
            avatar: personAvatar(w.id),
        }));
        return shuffle([correct, ...wrong]);
    }

    const others = eligible
        .filter(p => p.id !== correctId)
        .map(p => ({id: p.id, name: `${p.data.firstName || ''} ${p.data.lastName || ''}`.trim(), avatar: p.data.avatar || null}));
    const wrong = shuffle(others).slice(0, 3);
    return shuffle([correct, ...wrong]);
};

export const TriviaGame = ({onBack, onShowLeaderboard, onAddQuestion}) => {
    const navigate = useNavigate();
    const [phase, setPhase] = useState('loading');
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [score, setScore] = useState(0);
    const [hintUsed, setHintUsed] = useState(false);
    const [eliminatedOptions, setEliminatedOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [options, setOptions] = useState([]);
    const [showConfetti, setShowConfetti] = useState(false);
    const [streak, setStreak] = useState(0);
    const [results, setResults] = useState([]);
    const [playerName, setPlayerName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [leaderboard, setLeaderboard] = useState([]);
    const [slideDir, setSlideDir] = useState('in');
    const [instantFlip, setInstantFlip] = useState(false);
    const [showAdminAuth, setShowAdminAuth] = useState(false);
    const [adminCode, setAdminCode] = useState('');
    const [adminCodeError, setAdminCodeError] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [allItems, setAllItems] = useState([]);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const eligible = useMemo(() => getEligiblePeople(), []);


    useEffect(() => {
        const fetchItems = async () => {
            try {
                const snap = await get(ref(db, 'trivia/items'));
                const raw = snap.val();
                if (!raw) {
                    setPhase('empty');
                    return;
                }
                const arr = Object.entries(raw).map(([k, v]) => ({id: k, ...v}));
                const valid = arr.filter(item => item.aboutPerson || item.answerType === 'text');
                if (valid.length === 0) {
                    setPhase('empty');
                    return;
                }
                const shuffled = shuffle(valid).slice(0, MAX_QUESTIONS);
                setItems(shuffled);
                setOptions(buildOptions(shuffled[0], eligible));
                setPhase('playing');
            } catch (err) {
                console.error('Fetch failed:', err);
                setPhase('error');
            }
        };
        fetchItems();
    }, [eligible]);

    const currentItem = items[idx];
    const totalQ = items.length;
    const isLast = idx >= totalQ - 1;

    const handleSelect = useCallback((optionId) => {
        if (selected !== null) return;
        setSelected(optionId);
        const correctId = currentItem.answerType === 'text' ? '__correct__' : currentItem.aboutPerson;
        const correct = optionId === correctId;
        const pts = correct ? (hintUsed ? POINTS_HINT : POINTS_FULL) : 0;
        setScore(s => s + pts);
        setResults(r => [...r, {correct, hintUsed, pts}]);
        if (correct) {
            setStreak(s => s + 1);
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 1500);
        } else {
            setStreak(0);
        }
    }, [selected, currentItem, hintUsed]);

    const handleHint = useCallback(() => {
        if (hintUsed || selected !== null) return;
        setHintUsed(true);
        const correctId = currentItem.answerType === 'text' ? '__correct__' : currentItem.aboutPerson;
        const wrongOpts = options.filter(o => o.id !== correctId);
        const toEliminate = shuffle(wrongOpts).slice(0, 2).map(o => o.id);
        setEliminatedOptions(toEliminate);
    }, [hintUsed, selected, currentItem, options]);

    const handleSkip = useCallback(() => {
        if (selected !== null) return;
        setSelected('__skipped__');
        setStreak(0);
        setResults(r => [...r, {correct: false, hintUsed: false, pts: 0, skipped: true}]);
    }, [selected]);

    const handleRetry = useCallback(() => {
        setSelected(null);
        setHintUsed(false);
        setEliminatedOptions([]);
        setResults(r => r.slice(0, -1));
    }, []);

    const handleNext = useCallback(() => {
        if (isLast) {
            setPhase('gameOver');
            fetchLeaderboard();
            return;
        }
        setHintUsed(false);
        setEliminatedOptions([]);
        setSlideDir('out');
        setTimeout(() => {
            setInstantFlip(true);
            const next = idx + 1;
            setIdx(next);
            setSelected(null);
            setOptions(buildOptions(items[next], eligible));
            requestAnimationFrame(() => {
                setInstantFlip(false);
                setSlideDir('in');
            });
        }, 300);
    }, [isLast, idx, items, eligible]);

    const fetchLeaderboard = async () => {
        try {
            const snap = await get(query(ref(db, 'trivia/leaderboard'), orderByChild('score'), limitToLast(20)));
            const raw = snap.val() || {};
            const arr = Object.entries(raw)
                .map(([k, v]) => ({id: k, ...v}))
                .sort((a, b) => b.score - a.score);
            setLeaderboard(arr);
        } catch (e) {
            console.error(e);
        }
    };

    const submitScore = async () => {
        if (!playerName.trim()) return;
        setSubmitting(true);
        try {
            const entryRef = push(ref(db, 'trivia/leaderboard'));
            await set(entryRef, {
                name: playerName.trim(),
                score,
                totalQuestions: totalQ,
                timestamp: Date.now(),
            });
            setSubmitted(true);
            await fetchLeaderboard();
        } catch (e) {
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleAdminAuth = useCallback(async () => {
        if (adminCode.trim().toLowerCase() === 'kfir miller') {
            setShowAdminAuth(false);
            setAdminCode('');
            setAdminCodeError(false);
            try {
                const snap = await get(ref(db, 'trivia/items'));
                const raw = snap.val() || {};
                const arr = Object.entries(raw).map(([k, v]) => ({id: k, ...v}));
                arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                setAllItems(arr);
                setShowAdmin(true);
            } catch (e) {
                console.error('Failed to load items:', e);
            }
        } else {
            setAdminCodeError(true);
        }
    }, [adminCode]);

    const handleDeleteItem = useCallback(async (itemId) => {
        setDeletingId(itemId);
        try {
            await remove(ref(db, `trivia/items/${itemId}`));
            setAllItems(prev => prev.filter(i => i.id !== itemId));
            setConfirmDeleteId(null);
        } catch (e) {
            console.error('Delete failed:', e);
            alert('מחיקה נכשלה');
        } finally {
            setDeletingId(null);
        }
    }, []);

    const restart = () => {
        setPhase('loading');
        setIdx(0);
        setScore(0);
        setStreak(0);
        setHintUsed(false);
        setEliminatedOptions([]);
        setSelected(null);
        setResults([]);
        setPlayerName('');
        setSubmitted(false);
        setLeaderboard([]);
        const fetchAndStart = async () => {
            const snap = await get(ref(db, 'trivia/items'));
            const raw = snap.val();
            if (!raw) { setPhase('empty'); return; }
            const arr = Object.entries(raw).map(([k, v]) => ({id: k, ...v}));
            const valid = arr.filter(item => item.aboutPerson || item.answerType === 'text');
            if (valid.length === 0) { setPhase('empty'); return; }
            const shuffled = shuffle(valid).slice(0, MAX_QUESTIONS);
            setItems(shuffled);
            setOptions(buildOptions(shuffled[0], eligible));
            setPhase('playing');
        };
        fetchAndStart();
    };

    if (phase === 'loading') {
        return <CenterMsg><LoadingSpinner/><MsgText>טוען שאלות...</MsgText></CenterMsg>;
    }

    if (phase === 'empty') {
        return (
            <CenterMsg>
                <EmptyEmoji>📭</EmptyEmoji>
                <MsgText>אין עדיין שאלות טריוויה</MsgText>
                <MsgSub>הוסיפו את השאלה הראשונה!</MsgSub>
                <BtnRow>
                    <GameBtn onClick={onAddQuestion} $color="#8b5cf6">➕ הוסיפו שאלה</GameBtn>
                    <GameBtn onClick={onBack} $color="#64748b">← חזרה</GameBtn>
                </BtnRow>
            </CenterMsg>
        );
    }

    if (phase === 'error') {
        return (
            <CenterMsg>
                <MsgText>שגיאה בטעינה</MsgText>
                <GameBtn onClick={onBack} $color="#64748b">← חזרה</GameBtn>
            </CenterMsg>
        );
    }

    if (phase === 'gameOver') {
        const maxScore = totalQ * POINTS_FULL;
        const pct = Math.round((score / maxScore) * 100);
        const qualifies = !submitted && score > 0 && (leaderboard.length < 20 || score > (leaderboard[leaderboard.length - 1]?.score || 0));

        return (
            <GameOverWrap>
                {pct >= 70 && <ConfettiContainer>{Array.from({length: 40}).map((_, i) => (
                    <ConfettiPiece key={i} style={{
                        left: `${Math.random() * 100}%`,
                        backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                        animationDelay: `${Math.random() * 0.5}s`,
                        animationDuration: `${1.5 + Math.random() * 2}s`,
                    }}/>
                ))}</ConfettiContainer>}

                <GOTitle>סיום המשחק!</GOTitle>
                <ScoreBig>{score}</ScoreBig>
                <ScoreLabel>מתוך {maxScore} נקודות</ScoreLabel>

                <StatsRow>
                    <StatBox>
                        <StatNum>{results.filter(r => r.correct).length}</StatNum>
                        <StatLabel>תשובות נכונות</StatLabel>
                    </StatBox>
                    <StatBox>
                        <StatNum>{totalQ}</StatNum>
                        <StatLabel>שאלות</StatLabel>
                    </StatBox>
                    <StatBox>
                        <StatNum>{pct}%</StatNum>
                        <StatLabel>אחוז הצלחה</StatLabel>
                    </StatBox>
                </StatsRow>

                {qualifies && (
                    <LeaderboardEntry>
                        <EntryLabel>נכנסתם לטבלת המובילים! הכניסו שם:</EntryLabel>
                        <EntryRow>
                            <NameInput
                                value={playerName}
                                onChange={e => setPlayerName(e.target.value.slice(0, 16))}
                                placeholder="השם שלכם..."
                                maxLength={16}
                            />
                            <SubmitBtn onClick={submitScore} disabled={submitting || !playerName.trim()}>
                                {submitting ? '...' : 'שלחו'}
                            </SubmitBtn>
                        </EntryRow>
                    </LeaderboardEntry>
                )}

                {submitted && <SubmittedMsg>✅ הניקוד נשמר!</SubmittedMsg>}

                {leaderboard.length > 0 && (
                    <MiniBoard>
                        <MiniBoardTitle>🏆 טבלת האלופים</MiniBoardTitle>
                        {leaderboard.slice(0, 5).map((e, i) => (
                            <MiniRow key={e.id}>
                                <MiniRank>{i + 1}.</MiniRank>
                                <MiniName>{e.name}</MiniName>
                                <MiniScore>{e.score}</MiniScore>
                            </MiniRow>
                        ))}
                    </MiniBoard>
                )}

                <BtnRow style={{marginTop: 20}}>
                    <GameBtn onClick={restart} $color="#f59e0b">🔄 שחקו שוב</GameBtn>
                    <GameBtn onClick={onShowLeaderboard} $color="#10b981">🏆 טבלת האלופים</GameBtn>
                    <GameBtn onClick={onBack} $color="#64748b">← תפריט</GameBtn>
                </BtnRow>
            </GameOverWrap>
        );
    }

    const isSkipped = selected === '__skipped__';
    const correctAnswerId = currentItem.answerType === 'text' ? '__correct__' : currentItem.aboutPerson;
    const isTextType = currentItem.answerType === 'text';
    const isCorrectAnswer = selected !== null && !isSkipped && selected === correctAnswerId;
    const isWrongAnswer = selected !== null && !isSkipped && !isCorrectAnswer;
    const isFlipped = selected !== null && !isWrongAnswer;

    return (
        <GameWrap>
            <TopNavBar>
                <TopNavBtn onClick={() => { setShowAdminAuth(true); setAdminCode(''); setAdminCodeError(false); }}>⚙</TopNavBtn>
                <TopNavRight>
                    <TopNavBtn onClick={onShowLeaderboard}>🏆 טבלת האלופים</TopNavBtn>
                    <TopNavBtn onClick={onAddQuestion}>➕ הוסיפו שאלה</TopNavBtn>
                    <TopNavBtn onClick={onBack}>🏠 תפריט</TopNavBtn>
                </TopNavRight>
            </TopNavBar>
            <HUD>
                <HUDItem>שאלה {idx + 1}/{totalQ}</HUDItem>
                <HUDScore>⭐ {score}</HUDScore>
                {streak >= 2 && <StreakBadge>🔥 {streak} ברצף!</StreakBadge>}
            </HUD>

            {showAdminAuth && (
                <AdminOverlay onClick={() => setShowAdminAuth(false)}>
                    <AdminModal onClick={e => e.stopPropagation()}>
                        <AdminModalIcon>🔐</AdminModalIcon>
                        <AdminModalTitle>ניהול</AdminModalTitle>
                        <AdminCodeInput
                            value={adminCode}
                            onChange={e => { setAdminCode(e.target.value); setAdminCodeError(false); }}
                            onKeyDown={e => e.key === 'Enter' && handleAdminAuth()}
                            placeholder="הקלידו קוד..."
                            type="password"
                            autoFocus
                            $error={adminCodeError}
                        />
                        {adminCodeError && <AdminError>קוד שגוי</AdminError>}
                        <AdminModalBtnRow>
                            <AdminModalBtn $primary onClick={handleAdminAuth}>כניסה</AdminModalBtn>
                            <AdminModalBtn onClick={() => setShowAdminAuth(false)}>ביטול</AdminModalBtn>
                        </AdminModalBtnRow>
                    </AdminModal>
                </AdminOverlay>
            )}

            {showAdmin && (
                <AdminOverlay onClick={() => { setShowAdmin(false); setConfirmDeleteId(null); }}>
                    <AdminPanel onClick={e => e.stopPropagation()}>
                        <AdminPanelHeader>
                            <AdminPanelTitle>ניהול שאלות ({allItems.length})</AdminPanelTitle>
                            <AdminCloseBtn onClick={() => { setShowAdmin(false); setConfirmDeleteId(null); }}>✕</AdminCloseBtn>
                        </AdminPanelHeader>
                        <AdminTable>
                            {allItems.map(item => (
                                <AdminRow key={item.id}>
                                    <AdminRowInfo>
                                        <AdminRowQ dangerouslySetInnerHTML={{__html: item.question}}/>
                                        <AdminRowMeta>
                                            {item.answerType === 'text' ? item.textCorrect : personName(item.aboutPerson)} · {item.addedByName || 'לא ידוע'} · {new Date(item.createdAt).toLocaleDateString('he-IL')}
                                        </AdminRowMeta>
                                    </AdminRowInfo>
                                    {confirmDeleteId === item.id ? (
                                        <AdminConfirmDelete>
                                            <AdminConfirmLabel>למחוק?</AdminConfirmLabel>
                                            <AdminConfirmBtn
                                                $danger
                                                onClick={() => handleDeleteItem(item.id)}
                                                disabled={deletingId === item.id}
                                            >
                                                {deletingId === item.id ? '...' : '✓'}
                                            </AdminConfirmBtn>
                                            <AdminConfirmBtn onClick={() => setConfirmDeleteId(null)}>✕</AdminConfirmBtn>
                                        </AdminConfirmDelete>
                                    ) : (
                                        <AdminDeleteBtn onClick={() => setConfirmDeleteId(item.id)}>✕</AdminDeleteBtn>
                                    )}
                                </AdminRow>
                            ))}
                            {allItems.length === 0 && <AdminEmpty>אין שאלות</AdminEmpty>}
                        </AdminTable>
                    </AdminPanel>
                </AdminOverlay>
            )}

            {(isCorrectAnswer || isSkipped) && (
                <ActionBar>
                    <GlossyBtn onClick={handleNext}>
                        {isLast ? '🏁 סיכום' : '← שאלה הבאה'}
                    </GlossyBtn>
                </ActionBar>
            )}

            {isWrongAnswer && (
                <ActionBar>
                    <GlossyBtn onClick={handleRetry}>🔄 נסו שוב</GlossyBtn>
                    <GlossyBtn onClick={handleNext}>
                        {isLast ? '🏁 סיכום' : '← שאלה הבאה'}
                    </GlossyBtn>
                </ActionBar>
            )}

            {!isWrongAnswer && selected === null && (
                <ActionBar>
                    {!hintUsed && (
                        <GlossyBtn onClick={handleHint}>💡 רמז</GlossyBtn>
                    )}
                    <GlossyBtn onClick={handleSkip}>⏭️ דלג</GlossyBtn>
                </ActionBar>
            )}

            {showConfetti && (
                <ConfettiContainer>
                    {Array.from({length: 30}).map((_, i) => (
                        <ConfettiPiece key={i} style={{
                            left: `${Math.random() * 100}%`,
                            backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                            animationDelay: `${Math.random() * 0.3}s`,
                            animationDuration: `${1 + Math.random() * 1.5}s`,
                        }}/>
                    ))}
                </ConfettiContainer>
            )}

            <FlipCardContainer $slideDir={slideDir}>
                <FlipCardInner $flipped={isFlipped} $instant={instantFlip}>
                    <FlipCardFront>
                        {currentItem.question ? (
                            <QHtml dangerouslySetInnerHTML={{__html: currentItem.question}}/>
                        ) : (
                            <QText>{currentItem.text}</QText>
                        )}
                        <QAuthor>נוסף על ידי: {currentItem.addedByName}</QAuthor>

                        {isWrongAnswer && (
                            <WrongFeedback>❌ תשובה שגויה</WrongFeedback>
                        )}

                        {!isWrongAnswer && (
                            <OptionsGrid>
                                {options.map((opt, i) => {
                                    const eliminated = eliminatedOptions.includes(opt.id);
                                    return (
                                        <OptionCard
                                            key={opt.id}
                                            $delay={i * 0.06}
                                            $eliminated={eliminated}
                                            onClick={() => !eliminated && handleSelect(opt.id)}
                                            disabled={eliminated}
                                        >
                                            {!isTextType && (opt.avatar ? (
                                                <OptAvatar src={opt.avatar} alt=""/>
                                            ) : (
                                                <OptAvatarPlaceholder>👤</OptAvatarPlaceholder>
                                            ))}
                                            <OptName>{opt.name}</OptName>
                                        </OptionCard>
                                    );
                                })}
                            </OptionsGrid>
                        )}

                    </FlipCardFront>
                    <FlipCardBack $correct={isCorrectAnswer} $skipped={isSkipped}>
                        <CardHeader>{isSkipped ? '⏭️ דילגת' : '✅ תשובה נכונה!'}</CardHeader>
                        {currentItem.answer && (
                            <AnswerHtml dangerouslySetInnerHTML={{__html: currentItem.answer}}/>
                        )}
                        {currentItem.aboutPerson && (
                            <LinkedPerson onClick={() => navigate(`/?member=${currentItem.aboutPerson}`)}>
                                {personAvatar(currentItem.aboutPerson) ? (
                                    <LinkedAvatar src={personAvatar(currentItem.aboutPerson)} alt=""/>
                                ) : (
                                    <LinkedAvatarPlaceholder>👤</LinkedAvatarPlaceholder>
                                )}
                                <LinkedInfo>
                                    <LinkedName>{personName(currentItem.aboutPerson)}</LinkedName>
                                    <LinkedHint>לחצו למעבר לעץ המשפחה →</LinkedHint>
                                </LinkedInfo>
                            </LinkedPerson>
                        )}
                        <PointsLabel $correct={isCorrectAnswer} $skipped={isSkipped}>
                            {isCorrectAnswer
                                ? `+${hintUsed ? POINTS_HINT : POINTS_FULL} נקודות!`
                                : `התשובה הנכונה: ${isTextType ? currentItem.textCorrect : personName(currentItem.aboutPerson)}`
                            }
                        </PointsLabel>
                        <OptionsResult>
                            {options.map((opt) => {
                                const isCorrect = opt.id === correctAnswerId;
                                const isSelected = !isSkipped && selected === opt.id;
                                let variant = 'dimmed';
                                if (isCorrect) variant = 'correct';
                                else if (isSelected) variant = 'wrong';
                                return (
                                    <OptionResultPill key={opt.id} $variant={variant}>
                                        {!isTextType && (opt.avatar ? (
                                            <OptAvatarSmall src={opt.avatar} alt=""/>
                                        ) : (
                                            <OptAvatarSmallPlaceholder>👤</OptAvatarSmallPlaceholder>
                                        ))}
                                        <span>{opt.name}</span>
                                        {isCorrect && <OptBadge>✓</OptBadge>}
                                        {isSelected && !isCorrect && <OptBadge $wrong>✕</OptBadge>}
                                    </OptionResultPill>
                                );
                            })}
                        </OptionsResult>
                    </FlipCardBack>
                </FlipCardInner>
            </FlipCardContainer>

        </GameWrap>
    );
};

const CONFETTI_COLORS = ['#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#3b82f6', '#ec4899'];

const slideInAnim = keyframes`
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
`;

const slideOutAnim = keyframes`
    from { opacity: 1; transform: translateX(0); }
    to { opacity: 0; transform: translateX(30px); }
`;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
    0% { transform: scale(0); opacity: 0; }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
`;

const confettiFall = keyframes`
    0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const bounceIn = keyframes`
    0% { transform: scale(0.3); opacity: 0; }
    50% { transform: scale(1.05); }
    70% { transform: scale(0.9); }
    100% { transform: scale(1); opacity: 1; }
`;


const GameWrap = styled.div`
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 500px;
    padding: 50px 16px 40px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const ActionBar = styled.div`
    width: 100%;
    display: flex;
    gap: 10px;
    animation: ${fadeIn} 0.25s ease;
    box-sizing: border-box;
`;

const GlossyBtn = styled.button`
    flex: 1;
    padding: 12px 16px;
    border: 1px solid rgba(139, 92, 246, 0.5);
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(139, 92, 246, 0.4) 0%, rgba(88, 48, 196, 0.6) 100%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 12px rgba(88, 48, 196, 0.3);
    color: #e0d4fc;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    box-sizing: border-box;
    backdrop-filter: blur(6px);
    &:hover {
        background: linear-gradient(180deg, rgba(139, 92, 246, 0.55) 0%, rgba(88, 48, 196, 0.75) 100%);
        transform: translateY(-2px);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 6px 20px rgba(88, 48, 196, 0.4);
    }
`;

const WrongFeedback = styled.div`
    color: #fca5a5;
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    padding: 16px;
    animation: ${popIn} 0.3s ease;
`;

const HUD = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(8px);
    animation: ${fadeIn} 0.3s ease;
    box-sizing: border-box;
`;

const HUDItem = styled.span`
    color: rgba(241, 245, 249, 0.8);
    font-size: 14px;
    font-weight: 500;
`;

const HUDScore = styled.span`
    color: #fbbf24;
    font-size: 18px;
    font-weight: 700;
`;

const StreakBadge = styled.span`
    background: linear-gradient(135deg, #f97316, #ef4444);
    color: white;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 20px;
    animation: ${popIn} 0.4s ease;
`;

const FlipCardContainer = styled.div`
    width: 100%;
    perspective: 1200px;
    animation: ${p => p.$slideDir === 'in' ? slideInAnim : slideOutAnim} 0.35s ease;
`;

const FlipCardInner = styled.div`
    position: relative;
    width: 100%;
    transition: ${p => p.$instant ? 'none' : 'transform 0.6s ease'};
    transform-style: preserve-3d;
    transform: ${p => p.$flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const cardFaceBase = css`
    width: 100%;
    min-width: 0;
    backface-visibility: hidden;
    border-radius: 20px;
    padding: 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const FlipCardFront = styled.div`
    ${cardFaceBase}
    background: linear-gradient(145deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.1));
    border: none;
    box-shadow: 0 8px 32px rgba(139, 92, 246, 0.15), inset 0 0 0 2px rgba(139, 92, 246, 0.3);
`;

const FlipCardBack = styled.div`
    ${cardFaceBase}
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    transform: rotateY(180deg);
    border: none;
    background: ${p => {
        if (p.$correct) return 'linear-gradient(145deg, #0f3a2d, #133429)';
        if (p.$skipped) return 'linear-gradient(145deg, #1e2530, #232a36)';
        return 'linear-gradient(145deg, #2d1a1a, #2a1f1a)';
    }};
    box-shadow: ${p => {
        const outer = p.$correct ? 'rgba(16,185,129,0.2)' : p.$skipped ? 'rgba(100,116,139,0.15)' : 'rgba(239,68,68,0.15)';
        const inset = p.$correct ? 'rgba(16,185,129,0.5)' : p.$skipped ? 'rgba(148,163,184,0.35)' : 'rgba(239,68,68,0.4)';
        return `0 8px 32px ${outer}, inset 0 0 0 2px ${inset}`;
    }};
`;

const CardHeader = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: rgba(241, 245, 249, 0.7);
    text-align: center;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const PointsLabel = styled.div`
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    padding: 8px 16px;
    border-radius: 10px;
    margin-top: 4px;
    background: ${p => {
        if (p.$correct) return 'rgba(16, 185, 129, 0.15)';
        if (p.$skipped) return 'rgba(148, 163, 184, 0.1)';
        return 'rgba(239, 68, 68, 0.1)';
    }};
    color: ${p => {
        if (p.$correct) return '#a7f3d0';
        if (p.$skipped) return '#94a3b8';
        return '#fca5a5';
    }};
`;

const QText = styled.p`
    color: #f1f5f9;
    font-size: 18px;
    line-height: 1.7;
    margin: 0;
    min-width: 0;
    max-width: 100%;
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: break-word;
`;

const QHtml = styled.div`
    color: #f1f5f9;
    font-size: 18px;
    line-height: 1.7;
    direction: rtl;
    min-width: 0;
    max-width: 100%;
    word-break: keep-all;
    overflow-wrap: break-word;
    & p { margin: 0 0 8px; word-break: keep-all; overflow-wrap: break-word; }
    & img { max-width: 100%; border-radius: 8px; margin: 8px 0; display: block; }
    & strong { font-weight: 700; }
    & ul, & ol { padding-right: 20px; margin: 4px 0; }
    & * { word-break: keep-all !important; overflow-wrap: break-word !important; }
`;

const QAuthor = styled.span`
    color: rgba(241, 245, 249, 0.4);
    font-size: 12px;
`;

const AnswerHtml = styled.div`
    color: #f1f5f9;
    font-size: 16px;
    line-height: 1.6;
    direction: rtl;
    min-width: 0;
    max-width: 100%;
    word-break: keep-all;
    overflow-wrap: break-word;
    & p { margin: 0 0 6px; word-break: keep-all; overflow-wrap: break-word; }
    & img { max-width: 100%; border-radius: 8px; margin: 8px 0; display: block; }
    & strong { font-weight: 700; }
    & ul, & ol { padding-right: 20px; margin: 4px 0; }
    & * { word-break: keep-all !important; overflow-wrap: break-word !important; }
`;

const LinkedPerson = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(16, 185, 129, 0.25);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
        background: rgba(16, 185, 129, 0.12);
        transform: translateX(-4px);
        box-shadow: 0 4px 16px rgba(16, 185, 129, 0.15);
    }
`;

const LinkedAvatar = styled.img`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(16, 185, 129, 0.4);
    flex-shrink: 0;
`;

const LinkedAvatarPlaceholder = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(16, 185, 129, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
`;

const LinkedInfo = styled.div`
    flex: 1;
`;

const LinkedName = styled.div`
    color: #a7f3d0;
    font-size: 17px;
    font-weight: 700;
`;

const LinkedHint = styled.div`
    color: rgba(167, 243, 208, 0.5);
    font-size: 12px;
    margin-top: 2px;
`;



const OptionsGrid = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 20px;
`;

const OptionCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 18px 12px;
    border-radius: 16px;
    border: 2px solid ${p => p.$eliminated ? 'rgba(100, 100, 120, 0.15)' : 'rgba(139, 92, 246, 0.25)'};
    background: ${p => p.$eliminated ? 'rgba(30, 30, 50, 0.3)' : 'rgba(255, 255, 255, 0.06)'};
    color: ${p => p.$eliminated ? 'rgba(150, 150, 170, 0.3)' : '#f1f5f9'};
    cursor: ${p => p.$eliminated ? 'default' : 'pointer'};
    opacity: ${p => p.$eliminated ? 0.35 : 1};
    font-family: 'Heebo', sans-serif;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.3s;
    animation: ${fadeIn} 0.3s ease ${p => p.$delay}s both;
    backdrop-filter: blur(6px);
    box-shadow: ${p => p.$eliminated ? 'none' : '0 4px 16px rgba(0, 0, 0, 0.1)'};
    pointer-events: ${p => p.$eliminated ? 'none' : 'auto'};

    &:hover {
        background: ${p => p.$eliminated ? 'rgba(30, 30, 50, 0.3)' : 'rgba(139, 92, 246, 0.18)'};
        border-color: ${p => p.$eliminated ? 'rgba(100, 100, 120, 0.15)' : 'rgba(139, 92, 246, 0.6)'};
        transform: ${p => p.$eliminated ? 'none' : 'translateY(-3px)'};
        box-shadow: ${p => p.$eliminated ? 'none' : '0 8px 24px rgba(139, 92, 246, 0.2)'};
    }

    &:active {
        transform: ${p => p.$eliminated ? 'none' : 'translateY(0)'};
    }
`;

const OptionsResult = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    animation: ${fadeIn} 0.3s ease;
`;

const OptionResultPill = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 12px;
    font-family: 'Heebo', sans-serif;
    font-size: 13px;
    font-weight: 600;
    direction: rtl;

    ${p => p.$variant === 'correct' && css`
        background: rgba(16, 185, 129, 0.2);
        border: 2px solid rgba(16, 185, 129, 0.5);
        color: #a7f3d0;
    `}
    ${p => p.$variant === 'wrong' && css`
        background: rgba(239, 68, 68, 0.15);
        border: 2px solid rgba(239, 68, 68, 0.4);
        color: #fca5a5;
    `}
    ${p => p.$variant === 'dimmed' && css`
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.08);
        color: rgba(241, 245, 249, 0.35);
    `}
`;

const OptAvatar = styled.img`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(139, 92, 246, 0.3);
`;

const OptAvatarPlaceholder = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(139, 92, 246, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
`;

const OptName = styled.span`
    text-align: center;
    line-height: 1.3;
`;

const OptAvatarSmall = styled.img`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
`;

const OptAvatarSmallPlaceholder = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
`;

const OptBadge = styled.span`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 900;
    flex-shrink: 0;
    background: ${p => p.$wrong ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'};
    color: ${p => p.$wrong ? '#fca5a5' : '#a7f3d0'};
`;

const TopNavBar = styled.div`
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    direction: ltr;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    background: rgba(15, 12, 41, 0.55);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(139, 92, 246, 0.15);
    margin: -50px -16px 0;
    width: calc(100% + 32px);
    box-sizing: border-box;
`;

const TopNavRight = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const TopNavBtn = styled.button`
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(241, 245, 249, 0.65);
    font-family: 'Heebo', sans-serif;
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    line-height: 1.2;
    &:hover {
        color: #f1f5f9;
        background: rgba(139, 92, 246, 0.2);
        border-color: rgba(139, 92, 246, 0.4);
    }
`;



const ConfettiContainer = styled.div`
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 100;
    overflow: hidden;
`;

const ConfettiPiece = styled.div`
    position: absolute;
    top: -10px;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    animation: ${confettiFall} forwards;
`;

const CenterMsg = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 16px;
    padding: 20px;
    animation: ${fadeIn} 0.4s ease;
`;

const EmptyEmoji = styled.div`
    font-size: 56px;
`;

const MsgText = styled.div`
    color: #f1f5f9;
    font-size: 22px;
    font-weight: 700;
`;

const MsgSub = styled.div`
    color: rgba(241, 245, 249, 0.6);
    font-size: 16px;
`;

const BtnRow = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
`;

const GameBtn = styled.button`
    padding: 12px 24px;
    border: 2px solid ${p => p.$color};
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
        background: ${p => p.$color};
    }
`;

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 4px solid rgba(139, 92, 246, 0.2);
    border-top-color: #8b5cf6;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

const GameOverWrap = styled.div`
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 500px;
    padding: 30px 16px 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    animation: ${fadeIn} 0.5s ease;
`;

const GOTitle = styled.h2`
    color: #fbbf24;
    font-size: 32px;
    font-weight: 900;
    margin: 0;
    animation: ${bounceIn} 0.5s ease;
`;

const ScoreBig = styled.div`
    font-size: 72px;
    font-weight: 900;
    background: linear-gradient(135deg, #f59e0b, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${bounceIn} 0.6s ease 0.1s both;
`;

const ScoreLabel = styled.div`
    color: rgba(241, 245, 249, 0.6);
    font-size: 16px;
    margin-top: -10px;
`;

const StatsRow = styled.div`
    display: flex;
    gap: 20px;
    margin: 12px 0;
`;

const StatBox = styled.div`
    text-align: center;
    padding: 12px 18px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StatNum = styled.div`
    color: #f1f5f9;
    font-size: 24px;
    font-weight: 700;
`;

const StatLabel = styled.div`
    color: rgba(241, 245, 249, 0.5);
    font-size: 12px;
    margin-top: 4px;
`;

const LeaderboardEntry = styled.div`
    width: 100%;
    max-width: 380px;
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: ${popIn} 0.4s ease;
`;

const EntryLabel = styled.div`
    color: #fde68a;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
`;

const EntryRow = styled.div`
    display: flex;
    gap: 10px;
`;

const NameInput = styled.input`
    flex: 1;
    padding: 10px 14px;
    border: 2px solid rgba(245, 158, 11, 0.4);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    direction: rtl;
    outline: none;
    &:focus { border-color: #f59e0b; }
    &::placeholder { color: rgba(241, 245, 249, 0.3); }
`;

const SubmitBtn = styled.button`
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: #f59e0b;
    color: #1a1a2e;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:hover:not(:disabled) { background: #d97706; }
`;

const SubmittedMsg = styled.div`
    color: #a7f3d0;
    font-size: 16px;
    font-weight: 600;
    animation: ${popIn} 0.3s ease;
`;

const MiniBoard = styled.div`
    width: 100%;
    max-width: 380px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 14px;
    padding: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
`;

const MiniBoardTitle = styled.div`
    color: #fbbf24;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 12px;
`;

const MiniRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 4px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    &:last-child { border: none; }
`;

const MiniRank = styled.span`
    color: rgba(241, 245, 249, 0.5);
    font-size: 14px;
    width: 24px;
`;

const MiniName = styled.span`
    flex: 1;
    color: #f1f5f9;
    font-size: 15px;
`;

const MiniScore = styled.span`
    color: #fbbf24;
    font-size: 15px;
    font-weight: 700;
`;


const AdminOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ${fadeIn} 0.2s ease;
`;

const AdminModal = styled.div`
    background: linear-gradient(135deg, #1e1b4b, #312e81);
    border: 1px solid rgba(139, 92, 246, 0.4);
    border-radius: 20px;
    padding: 32px 24px;
    max-width: 360px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const AdminModalIcon = styled.div`
    font-size: 40px;
`;

const AdminModalTitle = styled.h3`
    color: #f1f5f9;
    font-size: 20px;
    font-weight: 800;
    margin: 0;
`;

const AdminCodeInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border: 2px solid ${p => p.$error ? 'rgba(239,68,68,0.6)' : 'rgba(139,92,246,0.4)'};
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 18px;
    text-align: center;
    direction: rtl;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;
    &:focus { border-color: ${p => p.$error ? '#ef4444' : '#8b5cf6'}; }
    &::placeholder { color: rgba(241, 245, 249, 0.3); }
`;

const AdminError = styled.div`
    color: #fca5a5;
    font-size: 14px;
    font-weight: 500;
`;

const AdminModalBtnRow = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;
    margin-top: 4px;
`;

const AdminModalBtn = styled.button`
    flex: 1;
    padding: 12px;
    border: ${p => p.$primary ? 'none' : '1px solid rgba(255,255,255,0.2)'};
    border-radius: 12px;
    background: ${p => p.$primary ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'rgba(255,255,255,0.05)'};
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
        ${p => p.$primary
    ? 'box-shadow: 0 0 20px rgba(139,92,246,0.4);'
    : 'background: rgba(255,255,255,0.1);'}
    }
`;

const AdminPanel = styled.div`
    background: linear-gradient(135deg, #1e1b4b, #1a1744);
    border: 1px solid rgba(139, 92, 246, 0.3);
    border-radius: 20px;
    max-width: 600px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    overflow: hidden;
`;

const AdminPanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid rgba(139, 92, 246, 0.2);
`;

const AdminPanelTitle = styled.h3`
    color: #f1f5f9;
    font-size: 18px;
    font-weight: 700;
    margin: 0;
`;

const AdminCloseBtn = styled.button`
    background: rgba(255, 255, 255, 0.08);
    border: none;
    color: rgba(241, 245, 249, 0.6);
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    &:hover { background: rgba(239, 68, 68, 0.2); color: #fca5a5; }
`;

const AdminTable = styled.div`
    overflow-y: auto;
    padding: 8px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    scrollbar-width: thin;
    scrollbar-color: rgba(139, 92, 246, 0.3) transparent;
`;

const AdminRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 12px;
    direction: rtl;
    transition: background 0.2s;
    &:hover { background: rgba(255, 255, 255, 0.07); }
`;

const AdminRowInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const AdminRowQ = styled.div`
    color: #f1f5f9;
    font-size: 14px;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    & img { display: none; }
    & p { margin: 0; }
`;

const AdminRowMeta = styled.div`
    color: rgba(241, 245, 249, 0.4);
    font-size: 12px;
    margin-top: 4px;
`;

const AdminDeleteBtn = styled.button`
    background: rgba(239, 68, 68, 0.12);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #fca5a5;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
    &:hover { background: rgba(239, 68, 68, 0.25); color: #f87171; }
`;

const AdminConfirmDelete = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const AdminConfirmLabel = styled.span`
    color: #fca5a5;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
`;

const AdminConfirmBtn = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: none;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    background: ${p => p.$danger ? 'rgba(239, 68, 68, 0.25)' : 'rgba(255, 255, 255, 0.08)'};
    color: ${p => p.$danger ? '#f87171' : 'rgba(241, 245, 249, 0.6)'};
    &:hover { background: ${p => p.$danger ? 'rgba(239, 68, 68, 0.4)' : 'rgba(255, 255, 255, 0.15)'}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const AdminEmpty = styled.div`
    color: rgba(241, 245, 249, 0.4);
    text-align: center;
    padding: 30px;
    font-size: 15px;
`;
